import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { slideInFromLeftWeighted } from '../../shared/component/Animation';
import { BlackFitBtn, PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { paperTextureModal } from '../../shared/component/paperTexture';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { BULK_UPLOAD_IMAGE_MAX_COUNT } from '../../shared/constants/limits';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getPageMessage } from '../../shared/constants/pageMessages';
import type { WorkSimpleWithDetails } from '../../shared/dto/EntityRepository';
import { AlertPosition, AlertType, ProjectElementType } from '../../shared/enum/EnumRepository';
import { useProjectDetailState } from '../../shared/hooks/useApi/useProjectDetail';
import { useProjectElementActions, useProjectElementState } from '../../shared/hooks/useApi/useProjectElement';
import { useCanShowProjectDetail } from '../../shared/hooks/useProjectVisibility';
import { useScrollContainerMarqueeSelect } from '../../shared/hooks/useScrollContainerMarqueeSelect';
import { useImportStationWorkStore } from '../../shared/store/importStationWorkStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useModalWorkTagStore } from '../../shared/store/workTagStore';
import { useModalPageStore } from '../../shared/store/pageStore';

import ImportTagStation from './ImportTagStation';
import PageAboutWorkImport from './PageAboutWorkImport';
import SelectedDataThumb from './SelectedDataThumb';
import SimpleWorkForPE, { simpleWorkRowToImportPayload } from './SimpleWorkForPE';

const getImportWorkItemId = (row: WorkSimpleWithDetails) => String(row.id);

const ImportStationWorks: React.FC = () => {
  const { isClosing } = useModalStore();
  const { selectedDataList, clearSelectedDataList, clearSimpleList } = useImportStationWorkStore();
  const { project } = useProjectDetailState();
  const canShowProject = useCanShowProjectDetail();

  const { clearModal } = useModalStore();
  const { simpleList } = useImportStationWorkStore();
  const { projectElementList } = useProjectElementState();
  const { importWorkOrDetailAsPeBulk } = useProjectElementActions();
  const { enqueueAlert } = useStandardAlertStore();
  const { selectedModalTags, clearSelectedModalTags } = useModalWorkTagStore();
  const resetModalPage = useModalPageStore((s) => s.resetModalPage);
  const listRef = useRef<HTMLDivElement>(null);

  const clearImportModalState = () => {
    clearSelectedDataList();
    clearSimpleList();
    clearSelectedModalTags();
    resetModalPage();
  };

  useEffect(() => {
    return () => {
      clearImportModalState();
    };
  }, []);

  const handleSubmit = async () => {
    if (!project) return;
    if (selectedDataList.length === 0){
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_IMPORT_EMPTY'),
      });
      return;
    }
    const workIdsInProject = new Set(
      projectElementList
        .filter((pe) => pe.projectElementType === ProjectElementType.WORK)
        .map((pe) => (pe as { work: { id: string } }).work.id)
    );
    const detailIdsInProject = new Set(
      projectElementList
        .filter((pe) => pe.projectElementType === ProjectElementType.DETAIL)
        .map((pe) => (pe as { workDetail: { id: string } }).workDetail.id)
    );
    const workIdList = selectedDataList
      .filter((item) => item.detailId == null && !workIdsInProject.has(item.workId))
      .map((item) => item.workId);
    const workDetailIdList = selectedDataList
      .filter((item) => item.detailId != null && !detailIdsInProject.has(item.detailId!))
      .map((item) => item.detailId!);
    if (workIdList.length === 0 && workDetailIdList.length === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_IMPORT_EMPTY'),
      });
      return;
    }
    await importWorkOrDetailAsPeBulk({
      projectId: project.id,
      workIdList,
      workDetailIdList,
    });
  };

  const handleCancel = () => {
    clearImportModalState();
    clearModal();
  }

  const commitMarquee = useCallback(
    (hit: WorkSimpleWithDetails[]) => {
      if (hit.length === 0) return;
      const workIdsInProject = new Set(
        projectElementList
          .filter((pe) => pe.projectElementType === ProjectElementType.WORK)
          .map((pe) => (pe as { work: { id: string } }).work.id)
      );
      let showedAlert = false;
      for (const row of hit) {
        const { selectedDataList, addSelectedData } = useImportStationWorkStore.getState();
        if (workIdsInProject.has(row.id)) continue;
        const taken = selectedDataList.some(
          (s) => String(s.workId) === String(row.id) && s.detailId === undefined
        );
        if (taken) continue;
        if (selectedDataList.length >= BULK_UPLOAD_IMAGE_MAX_COUNT) {
          if (!showedAlert) {
            enqueueAlert({
              type: AlertType.ALERT,
              position: AlertPosition.TOP,
              content: getAlertMessage('BULK_IMPORT_MAX_EXCEEDED', {
                maxCount: BULK_UPLOAD_IMAGE_MAX_COUNT,
              }),
            });
            showedAlert = true;
          }
          break;
        }
        addSelectedData(simpleWorkRowToImportPayload(row));
      }
    },
    [enqueueAlert, projectElementList]
  );

  const {
    handleContainerMouseDown,
    handleContainerClickCapture,
    marqueeBox,
    marqueeSelecting,
    hitItems,
  } = useScrollContainerMarqueeSelect({
    items: simpleList,
    getItemId: getImportWorkItemId,
    containerRef: listRef,
    enabled: simpleList.length > 0,
    onCommit: commitMarquee,
  });

  const hitIdSet = useMemo(() => new Set(hitItems.map((w) => String(w.id))), [hitItems]);

  if (!canShowProject || !project) return null;

  return (
    <LeftModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <ImportComp>
        <ImportMainTitleWrapper>
          <ImportTitle>{getModalMessage('SELECT_TO_IMPORT_WORK_DETAIL')}</ImportTitle>
          <ImportSubTitle />
        </ImportMainTitleWrapper>
        <ContentRow>
          <TagSlot data-suppress-marquee>
            <ImportTagStation />
          </TagSlot>
          <ImportContentContainer
              ref={listRef}
              onMouseDown={handleContainerMouseDown}
              onClickCapture={handleContainerClickCapture}
              $marqueeSelecting={marqueeSelecting}
            >
              {simpleList.length === 0 ? (
                <NoContentContainer>
                  {selectedModalTags.length > 0
                    ? getPageMessage('IMPORT_STATION_NO_WORK_FOR_SELECTED_TAGS')
                    : getPageMessage('IMPORT_STATION_EMPTY_WORK')}
                </NoContentContainer>
              ) : (
                simpleList.map((sw) => (
                  <SimpleContentComp key={sw.id}>
                    <SimpleWorkForPE data={sw} isMarqueeHit={hitIdSet.has(String(sw.id))} />
                  </SimpleContentComp>
                ))
              )}
              {marqueeBox ? (
                <MarqueeSelectionBox
                  style={{
                    left: marqueeBox.left,
                    top: marqueeBox.top,
                    width: marqueeBox.width,
                    height: marqueeBox.height,
                  }}
                />
              ) : null}
            </ImportContentContainer>
          <PageSlot>
            <PageAboutWorkImport />
          </PageSlot>
          <SelectedDataContainer $isEmpty={selectedDataList.length === 0}>
            {selectedDataList.length === 0 ? (
              <SelectedDataPlaceholder>
                {selectedModalTags.length > 0 && simpleList.length === 0
                  ? getPageMessage('IMPORT_STATION_NO_WORK_FOR_SELECTED_TAGS')
                  : getModalMessage('SELECT_TO_IMPORT_EMPTY')}
              </SelectedDataPlaceholder>
            ) : (
              selectedDataList.map((item, i) => (
                <SelectedDataThumb
                  key={`${item.workId}-${item.detailId ?? 'work'}-${i}`}
                  item={item}
                  layout="stack"
                />
              ))
            )}
          </SelectedDataContainer>
        </ContentRow>
        <ButtonContainer>
          <HeadlessBtn
            value={getBtnName('IMPORT')}
            handleClick={handleSubmit}
            StyledBtn={BlackFitBtn}
          />
          <HeadlessBtn
            value={getBtnName('CANCEL')}
            handleClick={handleCancel}
            StyledBtn={PaperBlurFitBtn}
          />
        </ButtonContainer>
      </ImportComp>
    </LeftModalFrame>
  );
};

export const LeftModalFrame = styled.div<{ $isClosing: boolean; }>`
  position: absolute;
  left: 4vw;
  width: calc(40vw + 200px);
  min-width: 700px;
  max-width: 820px;
  height: 88vh;

  padding: 20px;
  

  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) =>
    $isClosing
      ? 'translateX(-100%)'
      : 'translateX(0)'
  };

  transition: all 0.6s ease-out;
  ${paperTextureModal}
  ${slideInFromLeftWeighted}
`;

const ImportComp = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ContentRow = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 200px;
  grid-template-rows: auto minmax(0, 1fr) auto;
  column-gap: 3vh;
`;

const TagSlot = styled.div`
  grid-column: 1;
  grid-row: 1;
`;

const PageSlot = styled.div`
  grid-column: 1;
  grid-row: 3;
`;


const ImportMainTitleWrapper = styled.div`
  width: 100%;
  height: 5vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const ImportTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const ImportSubTitle = styled.div`
  margin-top: 6px;
  text-decoration: underline;
  font-style: italic;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_01};
  ${({ theme }) => theme.typography.Body_04};
`;

const SelectedDataContainer = styled.div<{ $isEmpty?: boolean }>`
  grid-column: 2;
  grid-row: 2;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: ${({ $isEmpty }) => ($isEmpty ? 'center' : 'flex-start')};
  gap: 8px;
  overflow-y: auto;
  border-top: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SelectedDataPlaceholder = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const ImportContentContainer = styled.div<{ $marqueeSelecting?: boolean }>`
  grid-column: 1;
  grid-row: 2;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: scroll;
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  user-select: ${({ $marqueeSelecting }) => ($marqueeSelecting ? 'none' : 'auto')};
  &::-webkit-scrollbar {
    display: none;
  }
`;

const MarqueeSelectionBox = styled.div`
  position: absolute;
  pointer-events: none;
  box-sizing: border-box;
  border: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  background: transparent;
  backdrop-filter: blur(1px);
  z-index: 2;
`;

const NoContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_03_2};
  white-space: pre-line;
`;

const SimpleContentComp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;



export default ImportStationWorks;
